/**
 * §58 PR 2 — Tenants → a hauler → Plan & Features (PLATFORM_ADMIN, MANAGE_HAULER_FEATURES).
 *
 *   - Template: the hauler's template and "Apply template…", showing what changes and
 *     which overrides stay (kept by default; templates are only starting defaults).
 *   - Features, by group: the effective state, where it comes from (template, override,
 *     trial until a date, always on), "Change…" and "Reset to template". Every change
 *     needs a reason, recorded in the audit history below.
 *   - History: this hauler's plan and feature changes, newest first.
 *
 * EXPRESS needs its one customer: choosing it in "Apply template…" hands over to
 * ExpressCustomerDialog (§58 PR 4), which sets the template and the customer together.
 * "Change customer…" re-points a hauler already on EXPRESS. Leaving EXPRESS is an ordinary
 * template change.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  FEATURE_GROUPS,
  FEATURE_TEMPLATES,
  type DirectTemplate,
  type FeatureTemplate,
  type HaulerFeatureView,
  type HaulerPlanView,
} from '@lodex-pro/shared/features';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  useApplyTemplate,
  useHaulerPlan,
  useResetFeature,
  useSetFeatureOverride,
} from './plan-hooks';
import {
  GROUP_LABELS,
  TEMPLATE_LABELS,
  historySummary,
  sourceLabel,
  stateLabel,
  templateDiff,
  trialEndIso,
  dateInZone,
  zoneLabel,
} from './plan-labels';
import { ExpressCustomerDialog } from './ExpressCustomerDialog';

const sectionStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-card)',
  padding: '16px 20px',
  marginBottom: '16px',
};

const errorText: React.CSSProperties = {
  fontSize: '13px',
  color: 'var(--color-error)',
  marginTop: '8px',
};

function ReasonField({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}): React.JSX.Element {
  return (
    <FormRow label="Reason (kept in the history)" htmlFor={id} required>
      <textarea
        id={id}
        rows={2}
        maxLength={500}
        style={{ ...fieldStyle, resize: 'vertical' }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </FormRow>
  );
}

// ── Apply template ─────────────────────────────────────────────────────────────

function ApplyTemplateDialog({
  plan,
  open,
  onOpenChange,
  onChooseExpress,
}: {
  plan: HaulerPlanView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** EXPRESS needs its customer: hand over to ExpressCustomerDialog with the overrides choice. */
  onChooseExpress: (clearOverrides: boolean) => void;
}): React.JSX.Element {
  const initial: FeatureTemplate = plan.template;
  const [template, setTemplate] = React.useState<FeatureTemplate>(initial);
  const [reason, setReason] = React.useState('');
  const [clear, setClear] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { mutation, store } = useApplyTemplate(plan.tenantId);

  React.useEffect(() => {
    if (open) {
      setTemplate(initial);
      setReason('');
      setClear(false);
      setError(null);
    }
  }, [open, initial]);

  const { changes, overridden } = templateDiff(plan.features, template);
  const busy = mutation.isPending;
  const unchanged = template === plan.template && !clear;
  // Already on EXPRESS: changing its customer is "Change customer…", not a template change.
  const toExpress = template === 'EXPRESS' && plan.template !== 'EXPRESS';

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (template === 'EXPRESS') {
      if (toExpress) onChooseExpress(clear);
      return;
    }
    const direct: DirectTemplate = template;
    mutation.mutate(
      { template: direct, reason: reason.trim(), clearOverrides: clear },
      {
        onSuccess: (res) => {
          store(res);
          toast.success(`Template set to ${TEMPLATE_LABELS[template]}.`);
          onOpenChange(false);
        },
        onError: () => setError('The template could not be applied. Try again.'),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ maxWidth: '520px' }}>
        <DialogHeader>
          <DialogTitle>Apply template</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit}>
          <FormRow label="Template" htmlFor="templateSelect" required>
            <select
              id="templateSelect"
              style={{ ...fieldStyle, cursor: 'pointer' }}
              value={template}
              onChange={(e) => setTemplate(e.target.value as FeatureTemplate)}
              disabled={busy}
            >
              {FEATURE_TEMPLATES.map((t) => (
                <option key={t} value={t}>
                  {TEMPLATE_LABELS[t]}
                  {t === 'EXPRESS' ? ' (one customer)' : ''}
                </option>
              ))}
            </select>
          </FormRow>

          <div style={{ fontSize: '13px', margin: '4px 0 12px' }} data-testid="template-diff">
            {changes.length === 0 ? (
              <p style={{ ...subText, textTransform: 'none' }}>No feature defaults change.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {changes.map((c) => (
                  <li key={c.id}>
                    {c.name}: {c.from} → {c.to}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {overridden.length > 0 && (
            <label style={{ display: 'flex', gap: '8px', fontSize: '13px', marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={clear}
                onChange={(e) => setClear(e.target.checked)}
                disabled={busy}
              />
              <span>
                Also clear {overridden.length} override(s) ({overridden.join(', ')}). Otherwise they
                stay in force.
              </span>
            </label>
          )}

          {template === 'EXPRESS' ? (
            <p style={{ ...subText, textTransform: 'none', margin: '0 0 12px' }}>
              {toExpress
                ? 'Next, choose their one customer. The reason is asked there.'
                : 'Already on this template. Use "Change customer…" to pick a different customer.'}
            </p>
          ) : (
            <ReasonField id="templateReason" value={reason} onChange={setReason} disabled={busy} />
          )}
          {error !== null && <p style={errorText}>{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            {template === 'EXPRESS' ? (
              <Button type="submit" disabled={!toExpress}>
                Choose the customer…
              </Button>
            ) : (
              <Button type="submit" disabled={busy || reason.trim() === '' || unchanged}>
                {busy ? 'Applying…' : 'Apply'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Change one feature ─────────────────────────────────────────────────────────

function OverrideDialog({
  tenantId,
  feature,
  timezone,
  onClose,
}: {
  tenantId: string;
  feature: HaulerFeatureView;
  /** The hauler's company timezone: a trial ends at the end of a day THERE (DEC-004). */
  timezone: string;
  onClose: () => void;
}): React.JSX.Element {
  // An override still in force is the starting point, so editing one field never silently
  // changes another (#876 review): a "just raise the limit" edit keeps the state and the
  // trial end. With no override, the current state is the starting point.
  const active = feature.override !== null && !feature.override.expired ? feature.override : null;
  const [enabled, setEnabled] = React.useState(active?.enabled ?? feature.effective.enabled);
  const [limit, setLimit] = React.useState(
    String(active?.limit ?? feature.effective.limit ?? feature.templateDefault.limit ?? ''),
  );
  // How long it lasts is an explicit choice, never an empty field read as "forever".
  const [endMode, setEndMode] = React.useState<'permanent' | 'trial' | null>(
    active === null ? null : active.expiresAt === null ? 'permanent' : 'trial',
  );
  const [trialDate, setTrialDate] = React.useState(
    active?.expiresAt != null ? dateInZone(active.expiresAt, timezone) : '',
  );
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const { mutation, store } = useSetFeatureOverride(tenantId, feature.id);

  const needsLimit = feature.hasLimit && enabled;
  const limitValue = needsLimit ? Number(limit) : null;
  const limitOk =
    !needsLimit || (limit !== '' && Number.isInteger(limitValue) && (limitValue ?? -1) >= 0);
  const expiresAt = endMode === 'trial' ? trialEndIso(trialDate, timezone) : null;
  const trialOk =
    endMode === 'permanent' ||
    (endMode === 'trial' && expiresAt !== null && new Date(expiresAt).getTime() > Date.now());
  const busy = mutation.isPending;
  const zone = zoneLabel(timezone);

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    mutation.mutate(
      { enabled, limit: limitValue, expiresAt, reason: reason.trim() },
      {
        onSuccess: (res) => {
          store(res);
          toast.success(`${feature.name} is now ${enabled ? 'on' : 'off'} for this hauler.`);
          onClose();
        },
        onError: () => setError('The change could not be saved. Try again.'),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ maxWidth: '480px' }}>
        <DialogHeader>
          <DialogTitle>{feature.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit}>
          <p style={{ ...subText, textTransform: 'none', margin: '0 0 12px' }}>
            {feature.description} Template default:{' '}
            {stateLabel(feature.templateDefault.enabled, feature.templateDefault.limit)}.
          </p>
          <div
            role="radiogroup"
            aria-label="State"
            style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}
          >
            {[true, false].map((v) => (
              <label key={String(v)} style={{ display: 'flex', gap: '6px', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="featureState"
                  checked={enabled === v}
                  onChange={() => setEnabled(v)}
                  disabled={busy}
                />
                {v ? 'On' : 'Off'}
              </label>
            ))}
          </div>
          {needsLimit && (
            <FormRow label="Limit (active API keys)" htmlFor="featureLimit" required>
              <input
                id="featureLimit"
                type="number"
                min={0}
                step={1}
                style={fieldStyle}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                disabled={busy}
              />
            </FormRow>
          )}
          <div
            role="radiogroup"
            aria-label="How long"
            style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}
          >
            <label style={{ display: 'flex', gap: '6px', fontSize: '14px' }}>
              <input
                type="radio"
                name="featureEnd"
                checked={endMode === 'permanent'}
                onChange={() => setEndMode('permanent')}
                disabled={busy}
              />
              No end date (permanent)
            </label>
            <label style={{ display: 'flex', gap: '6px', fontSize: '14px', alignItems: 'center' }}>
              <input
                type="radio"
                name="featureEnd"
                checked={endMode === 'trial'}
                onChange={() => setEndMode('trial')}
                disabled={busy}
              />
              Trial: ends at the end of
              <input
                aria-label="Trial end date"
                type="date"
                style={{ ...fieldStyle, width: 'auto' }}
                value={trialDate}
                onChange={(e) => {
                  setTrialDate(e.target.value);
                  setEndMode('trial');
                }}
                disabled={busy}
              />
              <span style={{ color: 'var(--color-text-secondary)' }}>({zone})</span>
            </label>
          </div>
          {endMode === 'trial' && !trialOk && (
            <p style={errorText}>Pick a trial end date in the future.</p>
          )}
          <ReasonField id="overrideReason" value={reason} onChange={setReason} disabled={busy} />
          {error !== null && <p style={errorText}>{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || reason.trim() === '' || !limitOk || !trialOk}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Reset to template ──────────────────────────────────────────────────────────

function ResetDialog({
  tenantId,
  feature,
  onClose,
}: {
  tenantId: string;
  feature: HaulerFeatureView;
  onClose: () => void;
}): React.JSX.Element {
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const { mutation, store } = useResetFeature(tenantId, feature.id);
  const busy = mutation.isPending;

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    mutation.mutate(
      { reason: reason.trim() },
      {
        onSuccess: (res) => {
          store(res);
          toast.success(`${feature.name} follows the template again.`);
          onClose();
        },
        onError: () => setError('The reset could not be saved. Try again.'),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ maxWidth: '420px' }}>
        <DialogHeader>
          <DialogTitle>Reset {feature.name} to template</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit}>
          <p style={{ ...subText, textTransform: 'none', margin: '0 0 12px' }}>
            It will be{' '}
            {stateLabel(
              feature.templateDefault.enabled,
              feature.templateDefault.limit,
            ).toLowerCase()}
            , as the template says.
          </p>
          <ReasonField id="resetReason" value={reason} onChange={setReason} disabled={busy} />
          {error !== null && <p style={errorText}>{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || reason.trim() === ''}>
              {busy ? 'Resetting…' : 'Reset'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── The tab ────────────────────────────────────────────────────────────────────

function FeatureRow({
  f,
  timezone,
  onChange,
  onReset,
}: {
  f: HaulerFeatureView;
  timezone: string;
  onChange: () => void;
  onReset: () => void;
}): React.JSX.Element {
  return (
    <div
      data-testid={`feature-${f.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 90px 170px auto',
        gap: '12px',
        alignItems: 'center',
        padding: '8px 0',
        borderTop: '1px solid var(--color-border)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '14px' }}>{f.name}</div>
        <div style={{ ...subText, textTransform: 'none' }}>{f.description}</div>
      </div>
      <Pill
        label={stateLabel(f.effective.enabled, f.effective.limit)}
        tone={f.effective.enabled ? 'positive' : 'muted'}
      />
      <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
        {sourceLabel(f, timezone)}
      </span>
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
        {!f.isDataCapture && (
          <Button type="button" variant="outline" size="sm" onClick={onChange}>
            Change…
          </Button>
        )}
        {f.override !== null && (
          <Button type="button" variant="outline" size="sm" onClick={onReset}>
            Reset to template
          </Button>
        )}
      </div>
    </div>
  );
}

export function PlanFeaturesTab({ tenantId }: { tenantId: string }): React.JSX.Element {
  const q = useHaulerPlan(tenantId);
  const [applyOpen, setApplyOpen] = React.useState(false);
  const [changing, setChanging] = React.useState<HaulerFeatureView | null>(null);
  const [resetting, setResetting] = React.useState<HaulerFeatureView | null>(null);
  // Open, with the overrides choice carried from "Apply template…" (false from "Change customer…").
  const [express, setExpress] = React.useState<{ clearOverrides: boolean } | null>(null);

  if (q.isLoading) return <p style={subText}>Loading plan…</p>;
  const plan = q.data?.plan;
  if (q.isError || plan === undefined) {
    return <p style={errorText}>The plan could not be loaded.</p>;
  }

  return (
    <div>
      <section style={sectionStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div>
            <div style={subText}>Template</div>
            <div style={{ fontSize: '18px', fontWeight: 700 }} data-testid="plan-template">
              {TEMPLATE_LABELS[plan.template]}
            </div>
            {!plan.hasPlanRow && (
              <div style={{ ...subText, textTransform: 'none' }}>
                No plan set yet: this hauler reads as Starter.
              </div>
            )}
            {plan.expressOperator !== null && (
              <div style={{ fontSize: '13px' }}>Customer: {plan.expressOperator.name}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {plan.template === 'EXPRESS' && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setExpress({ clearOverrides: false })}
              >
                Change customer…
              </Button>
            )}
            <Button type="button" onClick={() => setApplyOpen(true)}>
              Apply template…
            </Button>
          </div>
        </div>
      </section>

      {FEATURE_GROUPS.map((g) => {
        const rows = plan.features.filter((f) => f.group === g);
        if (rows.length === 0) return null;
        return (
          <section key={g} style={sectionStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 4px' }}>
              {GROUP_LABELS[g]}
            </h3>
            {rows.map((f) => (
              <FeatureRow
                key={f.id}
                f={f}
                timezone={plan.timezone}
                onChange={() => setChanging(f)}
                onReset={() => setResetting(f)}
              />
            ))}
          </section>
        );
      })}

      <section style={sectionStyle}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 8px' }}>History</h3>
        {plan.history.length === 0 ? (
          <p style={{ ...subText, textTransform: 'none' }}>No changes yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="plan-history">
            {plan.history.map((h, i) => (
              <li key={`${h.at}-${String(i)}`} style={{ fontSize: '13px', padding: '4px 0' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {new Date(h.at).toLocaleString()} · {h.byName ?? 'System'} ·{' '}
                </span>
                {historySummary(h, plan.timezone)}
                {h.reason !== null && h.reason !== '' && (
                  <span style={{ color: 'var(--color-text-secondary)' }}> — {h.reason}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <ApplyTemplateDialog
        plan={plan}
        open={applyOpen}
        onOpenChange={setApplyOpen}
        onChooseExpress={(clearOverrides) => {
          setApplyOpen(false);
          setExpress({ clearOverrides });
        }}
      />
      <ExpressCustomerDialog
        open={express !== null}
        tenantId={tenantId}
        currentOperator={plan.expressOperator}
        clearOverrides={express?.clearOverrides ?? false}
        onClose={() => setExpress(null)}
        onSaved={() => undefined}
      />
      {changing !== null && (
        <OverrideDialog
          tenantId={tenantId}
          feature={changing}
          timezone={plan.timezone}
          onClose={() => setChanging(null)}
        />
      )}
      {resetting !== null && (
        <ResetDialog tenantId={tenantId} feature={resetting} onClose={() => setResetting(null)} />
      )}
    </div>
  );
}
